/**
 * Gestionnaire de déduplication de requêtes
 * Permet d'éviter les appels API redondants pour la même ressource
 * en réutilisant les promesses en cours
 */

class RequestDeduplicator {
  constructor() {
    this.pendingRequests = new Map();
    this.requestCounters = new Map();
  }

  /**
   * Exécute une fonction de fetch en évitant les doublons
   * @param {string} key - Clé unique identifiant la requête
   * @param {Function} fetchFn - Fonction async qui retourne une Promise
   * @param {Object} options - Options de configuration
   * @returns {Promise} - Résultat de la requête
   */
  async fetch(key, fetchFn, options = {}) {
    const { timeout = 30000, debug = false } = options;

    // Vérifier si une requête est déjà en cours pour cette clé
    if (this.pendingRequests.has(key)) {
      if (debug) {
        const count = this.requestCounters.get(key) || 0;
        console.log(`[RequestDedup] Réutilisation requête en cours pour "${key}" (${count} appels évités)`);
        this.requestCounters.set(key, count + 1);
      }
      return this.pendingRequests.get(key);
    }

    // Créer une nouvelle promesse avec timeout
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`Request timeout for key: ${key}`)), timeout);
    });

    const promise = Promise.race([
      fetchFn(),
      timeoutPromise
    ]).finally(() => {
      // Nettoyer la requête une fois terminée
      this.pendingRequests.delete(key);
      
      if (debug) {
        const count = this.requestCounters.get(key) || 0;
        if (count > 0) {
          console.log(`[RequestDedup] Requête "${key}" terminée - ${count} appels dupliqués évités`);
        }
        this.requestCounters.delete(key);
      }
    });

    // Stocker la promesse en cours
    this.pendingRequests.set(key, promise);
    this.requestCounters.set(key, 0);

    return promise;
  }

  /**
   * Annule toutes les requêtes en cours (utile au démontage des composants)
   */
  cancelAll() {
    this.pendingRequests.clear();
    this.requestCounters.clear();
  }

  /**
   * Annule une requête spécifique
   * @param {string} key - Clé de la requête à annuler
   */
  cancel(key) {
    this.pendingRequests.delete(key);
    this.requestCounters.delete(key);
  }

  /**
   * Vérifie si une requête est en cours
   * @param {string} key - Clé de la requête
   * @returns {boolean}
   */
  isPending(key) {
    return this.pendingRequests.has(key);
  }

  /**
   * Obtient le nombre de requêtes en cours
   * @returns {number}
   */
  getPendingCount() {
    return this.pendingRequests.size;
  }

  /**
   * Obtient les statistiques d'économie de requêtes
   * @returns {Object}
   */
  getStats() {
    const stats = {};
    this.requestCounters.forEach((count, key) => {
      if (count > 0) {
        stats[key] = count;
      }
    });
    return stats;
  }
}

// Instance singleton partagée globalement
const globalDeduplicator = new RequestDeduplicator();

export default globalDeduplicator;

/**
 * Hook React pour utiliser le deduplicator avec nettoyage automatique
 * @returns {RequestDeduplicator}
 */
export function useRequestDeduplicator() {
  return globalDeduplicator;
}
